import { useEffect, useRef } from 'react'

// ── FOREST: Dense leaf storm ───────────────────────────────────────────────────
function ForestTransition({ onDone }) {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    const W = canvas.width = window.innerWidth
    const H = canvas.height = window.innerHeight

    const COLORS = ['#4ade80','#86efac','#22c55e','#a3e635','#bbf7d0','#fbbf24','#16a34a','#65a30d','#d9f99d','#f59e0b']

    // 200 leaves — spread across multiple rows and starting positions
    const leaves = Array.from({ length: 200 }, (_, i) => ({
      x: -40 - (i % 50) * 38 - Math.random() * 60,
      y: Math.random() * (H + 100) - 50,
      size: Math.random() * 20 + 5,
      speed: Math.random() * 10 + 5,
      vy: (Math.random() - 0.4) * 3.5,
      rot: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.18,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      opacity: Math.random() * 0.65 + 0.45,
      wobble: Math.random() * Math.PI * 2,
      wobbleSpeed: Math.random() * 0.06 + 0.02,
    }))

    let frame = 0
    let raf
    let coverAlpha = 0
    let covering = false

    function drawLeaf(x, y, size, rot, color, opacity) {
      ctx.save()
      ctx.globalAlpha = opacity
      ctx.translate(x, y)
      ctx.rotate(rot)
      ctx.beginPath()
      ctx.moveTo(0, -size)
      ctx.bezierCurveTo(size * 0.85, -size * 0.5, size * 0.85, size * 0.5, 0, size)
      ctx.bezierCurveTo(-size * 0.85, size * 0.5, -size * 0.85, -size * 0.5, 0, -size)
      ctx.fillStyle = color
      ctx.fill()
      // stem
      ctx.beginPath()
      ctx.moveTo(0, size * 0.8)
      ctx.lineTo(0, size * 1.3)
      ctx.strokeStyle = 'rgba(0,80,0,0.35)'
      ctx.lineWidth = 1
      ctx.stroke()
      ctx.restore()
    }

    function draw() {
      ctx.clearRect(0, 0, W, H)
      frame++

      leaves.forEach(l => {
        l.wobble += l.wobbleSpeed
        l.x += l.speed + Math.sin(l.wobble) * 1.2
        l.y += l.vy + Math.cos(l.wobble) * 0.6
        l.rot += l.rotSpeed
        drawLeaf(l.x, l.y, l.size, l.rot, l.color, l.opacity)
      })

      // Cover starts later to let the full leaf burst show
      if (frame > 55 && !covering) covering = true
      if (covering) {
        coverAlpha = Math.min(1, coverAlpha + 0.038)
        ctx.save()
        ctx.globalAlpha = coverAlpha
        ctx.fillStyle = '#071a07'
        ctx.fillRect(0, 0, W, H)
        ctx.restore()
      }

      if (coverAlpha >= 1) { cancelAnimationFrame(raf); onDone(); return }
      raf = requestAnimationFrame(draw)
    }
    draw()
    return () => cancelAnimationFrame(raf)
  }, [])

  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none' }} />
}

// ── OCEAN: Tsunami wave + fish + deep sea bioluminescence ─────────────────────
function OceanTransition({ onDone }) {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    const W = canvas.width = window.innerWidth
    const H = canvas.height = window.innerHeight

    let waveY = H + 160
    let t = 0
    let raf

    // Multiple wave layers
    const waveConfigs = [
      { offset: 0,   amp: 35, freq: 4.5, speed: 1.8, color: 'rgba(56,189,248,0.9)',   vy: 22 },
      { offset: 30,  amp: 25, freq: 3.5, speed: 1.3, color: 'rgba(14,165,233,0.85)',  vy: 20 },
      { offset: 60,  amp: 18, freq: 6,   speed: 2.2, color: 'rgba(2,132,199,0.8)',    vy: 18 },
      { offset: 90,  amp: 12, freq: 8,   speed: 3,   color: 'rgba(7,89,133,0.75)',    vy: 16 },
    ]

    // Bubbles
    const bubbles = Array.from({ length: 60 }, () => ({
      x: Math.random() * W,
      y: H + Math.random() * 100,
      r: Math.random() * 6 + 1,
      speed: Math.random() * 3 + 1,
      drift: (Math.random() - 0.5) * 1,
      opacity: Math.random() * 0.5 + 0.2,
    }))

    // Fish shapes
    const fish = Array.from({ length: 18 }, () => ({
      x: Math.random() * W,
      y: H + Math.random() * 200,
      vx: (Math.random() - 0.5) * 4 + 2,
      vy: -(Math.random() * 1 + 0.5),
      size: Math.random() * 12 + 5,
      color: ['rgba(186,230,253,0.8)','rgba(125,211,252,0.7)','rgba(56,189,248,0.6)','rgba(248,250,252,0.5)'][Math.floor(Math.random() * 4)],
      flip: Math.random() > 0.5 ? 1 : -1,
    }))

    // Light rays
    const rays = Array.from({ length: 8 }, (_, i) => ({
      x: W * (0.05 + i * 0.12),
      width: 30 + Math.random() * 60,
      speed: Math.random() * 0.4 + 0.2,
      phase: Math.random() * Math.PI * 2,
    }))

    function drawFish(f) {
      ctx.save()
      ctx.translate(f.x, f.y)
      ctx.scale(f.flip, 1)
      ctx.globalAlpha = 0.7
      ctx.fillStyle = f.color
      // Body
      ctx.beginPath()
      ctx.ellipse(0, 0, f.size, f.size * 0.45, 0, 0, Math.PI * 2)
      ctx.fill()
      // Tail
      ctx.beginPath()
      ctx.moveTo(-f.size * 0.9, 0)
      ctx.lineTo(-f.size * 1.5, -f.size * 0.4)
      ctx.lineTo(-f.size * 1.5, f.size * 0.4)
      ctx.closePath()
      ctx.fill()
      // Eye
      ctx.fillStyle = 'rgba(0,0,0,0.4)'
      ctx.beginPath()
      ctx.arc(f.size * 0.5, -f.size * 0.1, f.size * 0.08, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    }

    function draw() {
      ctx.clearRect(0, 0, W, H)
      t += 0.04

      // Moving light rays from above
      rays.forEach(r => {
        const rx = r.x + Math.sin(t * r.speed + r.phase) * 40
        const currentWaveY = waveY + waveConfigs[0].offset
        if (currentWaveY < H) {
          ctx.save()
          ctx.globalAlpha = 0.06 + Math.sin(t * r.speed + r.phase) * 0.02
          const rg = ctx.createLinearGradient(rx, currentWaveY, rx + r.width, H)
          rg.addColorStop(0, 'rgba(186,230,253,1)')
          rg.addColorStop(1, 'rgba(56,189,248,0)')
          ctx.beginPath()
          ctx.moveTo(rx, currentWaveY)
          ctx.lineTo(rx + r.width * 0.3, currentWaveY)
          ctx.lineTo(rx + r.width, H)
          ctx.lineTo(rx + r.width * 0.7, H)
          ctx.closePath()
          ctx.fillStyle = rg
          ctx.fill()
          ctx.restore()
        }
      })

      // Wave layers (back to front)
      waveConfigs.slice().reverse().forEach((wc, idx) => {
        const wy = waveY - wc.offset
        ctx.save()
        ctx.beginPath()
        ctx.moveTo(0, H)
        ctx.lineTo(0, wy)
        for (let x = 0; x <= W; x += 4) {
          const y = wy + Math.sin((x / W) * Math.PI * wc.freq + t * wc.speed) * wc.amp
                      + Math.sin((x / W) * Math.PI * (wc.freq * 0.6) - t * wc.speed * 0.7) * (wc.amp * 0.4)
          ctx.lineTo(x, y)
        }
        ctx.lineTo(W, H)
        ctx.closePath()
        const grad = ctx.createLinearGradient(0, wy, 0, H)
        grad.addColorStop(0, wc.color)
        grad.addColorStop(0.5, `rgba(2,${80 + idx * 10},${140 + idx * 15},0.9)`)
        grad.addColorStop(1, `rgba(0,${20 + idx * 5},${40 + idx * 8},1)`)
        ctx.fillStyle = grad
        ctx.fill()
        ctx.restore()
      })

      // Seafoam crest on top wave
      ctx.save()
      ctx.globalAlpha = 0.4
      for (let x = 0; x <= W; x += 20) {
        const wy = waveY + Math.sin((x / W) * Math.PI * waveConfigs[0].freq + t * waveConfigs[0].speed) * waveConfigs[0].amp
        ctx.beginPath()
        ctx.arc(x, wy - 4, Math.random() * 4 + 2, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(240,249,255,0.6)'
        ctx.fill()
      }
      ctx.restore()

      // Fish swimming up
      fish.forEach(f => {
        f.x += f.vx
        f.y -= Math.abs(f.vy) + 0.5
        if (f.x > W + 50) f.x = -50
        if (f.x < -50) f.x = W + 50
        drawFish(f)
      })

      // Bubbles rising
      bubbles.forEach(b => {
        b.y -= b.speed
        b.x += b.drift + Math.sin(t * 2 + b.x) * 0.3
        ctx.save()
        ctx.globalAlpha = b.opacity * Math.max(0, (H - b.y) / H)
        ctx.beginPath()
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2)
        ctx.strokeStyle = 'rgba(186,230,253,0.8)'
        ctx.lineWidth = 0.8
        ctx.stroke()
        // Highlight
        ctx.beginPath()
        ctx.arc(b.x - b.r * 0.3, b.y - b.r * 0.3, b.r * 0.25, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(255,255,255,0.4)'
        ctx.fill()
        ctx.restore()
      })

      waveY -= 10

      if (waveY < -200) {
        ctx.fillStyle = '#020d1a'
        ctx.fillRect(0, 0, W, H)
        cancelAnimationFrame(raf)
        onDone()
        return
      }
      raf = requestAnimationFrame(draw)
    }
    draw()
    return () => cancelAnimationFrame(raf)
  }, [])

  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none' }} />
}

// ── FIRE: Smooth noise-based flame wall — no columns, no triangles ────────────
function FireTransition({ onDone }) {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    const W = canvas.width = window.innerWidth
    const H = canvas.height = window.innerHeight

    let riseY = H        // the baseline of the flame front, starts at bottom, moves up
    let t = 0
    let raf

    // Simple smooth noise: sum of sine waves at different frequencies/phases
    // Returns a value roughly in [-1, 1]
    function noise(x, tOff) {
      return (
        Math.sin(x * 0.008  + tOff * 1.1) * 0.40 +
        Math.sin(x * 0.019  - tOff * 1.7) * 0.25 +
        Math.sin(x * 0.041  + tOff * 2.3) * 0.18 +
        Math.sin(x * 0.083  - tOff * 3.1) * 0.10 +
        Math.sin(x * 0.160  + tOff * 4.7) * 0.07
      )
    }

    // Embers
    const embers = Array.from({ length: 120 }, () => ({
      x: Math.random() * W,
      y: H + Math.random() * 60,
      vx: (Math.random() - 0.5) * 2.5,
      vy: -(Math.random() * 5 + 1.5),
      life: Math.random(),
      decay: Math.random() * 0.006 + 0.002,
      size: Math.random() * 3 + 0.8,
      color: ['#ff4500','#ff6b35','#fbbf24','#ff8c00','#ff0000'][Math.floor(Math.random() * 5)],
    }))

    // Ash
    const ash = Array.from({ length: 45 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.8,
      vy: Math.random() * 0.5 + 0.1,
      size: Math.random() * 2.5 + 0.5,
      opacity: Math.random() * 0.35 + 0.08,
      rot: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.04,
    }))

    function drawFlameBand(baseY, amplitude, layerAlpha, colorBot, colorMid, colorTop) {
      // Draw ONE continuous wavy filled shape from baseY upward.
      // Top edge is defined by noise; bottom is a straight line at baseY.
      // This guarantees NO triangular gaps — it's a single seamless fill.
      ctx.save()
      ctx.beginPath()
      // Walk left → right along the flame top edge
      for (let x = 0; x <= W; x += 3) {
        const n = noise(x, t)
        const y = baseY + n * amplitude   // negative n = goes UP
        if (x === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      }
      // Close: right edge down, across the bottom, back up to start
      ctx.lineTo(W, H + 20)
      ctx.lineTo(0, H + 20)
      ctx.closePath()

      const grad = ctx.createLinearGradient(0, baseY - amplitude, 0, baseY + amplitude * 0.3)
      grad.addColorStop(0,   colorTop)
      grad.addColorStop(0.35, colorMid)
      grad.addColorStop(1,   colorBot)
      ctx.fillStyle = grad
      ctx.globalAlpha = layerAlpha
      ctx.fill()
      ctx.restore()
    }

    function draw() {
      ctx.clearRect(0, 0, W, H)
      t += 0.022

      // riseY climbs from H to -200 over ~4 seconds (≈240 frames at 60fps)
      riseY -= 4.5

      // ── Layer 1: deep red base (widest, tallest waviness) ──
      drawFlameBand(
        riseY + 60, 120,
        0.95,
        'rgba(120,10,0,1)',
        'rgba(200,30,0,0.9)',
        'rgba(255,60,0,0)'
      )

      // ── Layer 2: orange mid-flame ──
      drawFlameBand(
        riseY + 20, 90,
        0.85,
        'rgba(200,50,0,0.95)',
        'rgba(255,120,0,0.8)',
        'rgba(255,180,0,0)'
      )

      // ── Layer 3: yellow inner core ──
      drawFlameBand(
        riseY - 20, 65,
        0.70,
        'rgba(255,140,0,0.9)',
        'rgba(255,210,0,0.7)',
        'rgba(255,255,180,0)'
      )

      // ── Layer 4: bright white-yellow tip ──
      drawFlameBand(
        riseY - 55, 45,
        0.45,
        'rgba(255,220,80,0.8)',
        'rgba(255,255,200,0.5)',
        'rgba(255,255,255,0)'
      )

      // Floor glow
      if (riseY < H) {
        const fg = ctx.createLinearGradient(0, riseY + 80, 0, H)
        fg.addColorStop(0, 'rgba(255,80,0,0)')
        fg.addColorStop(1, 'rgba(180,20,0,0.6)')
        ctx.fillStyle = fg
        ctx.fillRect(0, riseY + 80, W, H - riseY - 80)
      }

      // Embers
      embers.forEach(e => {
        e.x += e.vx + Math.sin(t * 4 + e.x * 0.015) * 0.6
        e.y += e.vy
        e.vy += 0.03
        e.life -= e.decay
        if (e.life <= 0) {
          e.x = Math.random() * W
          e.y = riseY + 40 + Math.random() * 80
          e.life = 0.8 + Math.random() * 0.4
          e.vy = -(Math.random() * 5 + 1.5)
          e.vx = (Math.random() - 0.5) * 2.5
        }
        const eg = ctx.createRadialGradient(e.x, e.y, 0, e.x, e.y, e.size * 3.5)
        eg.addColorStop(0, e.color)
        eg.addColorStop(0.5, 'rgba(255,100,0,0.2)')
        eg.addColorStop(1, 'rgba(0,0,0,0)')
        ctx.save()
        ctx.globalAlpha = e.life * 0.9
        ctx.beginPath()
        ctx.arc(e.x, e.y, e.size * 3.5, 0, Math.PI * 2)
        ctx.fillStyle = eg
        ctx.fill()
        ctx.beginPath()
        ctx.arc(e.x, e.y, e.size * 0.5, 0, Math.PI * 2)
        ctx.fillStyle = '#fffde7'
        ctx.fill()
        ctx.restore()
      })

      // Ash drifting down
      ash.forEach(a => {
        a.x += a.vx + Math.sin(t * 1.2 + a.x * 0.02) * 0.3
        a.y += a.vy
        a.rot += a.rotSpeed
        if (a.y > H + 20) { a.y = riseY - 20; a.x = Math.random() * W }
        ctx.save()
        ctx.globalAlpha = a.opacity
        ctx.translate(a.x, a.y)
        ctx.rotate(a.rot)
        ctx.beginPath()
        ctx.ellipse(0, 0, a.size, a.size * 0.4, 0, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(60,40,30,0.7)'
        ctx.fill()
        ctx.restore()
      })

      // Once flame front fully above screen, fill and done
      if (riseY < -260) {
        ctx.fillStyle = '#0f0500'
        ctx.fillRect(0, 0, W, H)
        cancelAnimationFrame(raf)
        onDone()
        return
      }
      raf = requestAnimationFrame(draw)
    }
    draw()
    return () => cancelAnimationFrame(raf)
  }, [])

  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none' }} />
}

// ── NEON: Pure fireworks show — big + small, no scan line ─────────────────────
function NeonTransition({ onDone }) {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    const W = canvas.width = window.innerWidth
    const H = canvas.height = window.innerHeight

    let t = 0
    let coverAlpha = 0
    let elapsed = 0          // counts frames; after ~220 frames fade out
    let raf

    const COLORS = ['#7c3aed','#06b6d4','#f472b6','#a855f7','#22d3ee','#e879f9','#38bdf8','#c084fc','#fbbf24','#34d399','#f87171','#ffffff']

    // ── Firework class with size scale ────────────────────────────────────────
    class Firework {
      constructor(scale) {
        this.scale = scale || (Math.random() > 0.6 ? 2.2 : Math.random() > 0.4 ? 1.4 : 0.7)
        this.reset()
      }
      reset() {
        this.x = W * 0.05 + Math.random() * W * 0.9
        this.y = H + 10
        // Big ones launch higher, small ones can be lower
        this.targetY = H * 0.05 + Math.random() * H * (this.scale > 1.5 ? 0.4 : 0.65)
        this.speed = (8 + Math.random() * 7) * (this.scale > 1.5 ? 1.1 : 0.9)
        this.exploded = false
        this.particles = []
        this.trail = []
        this.color = COLORS[Math.floor(Math.random() * COLORS.length)]
        this.color2 = COLORS[Math.floor(Math.random() * COLORS.length)]
        this.alive = true
        this.shape = ['circle','star','chrysanthemum','ring','willow'][Math.floor(Math.random() * 5)]
      }

      explode() {
        this.exploded = true
        const s = this.scale
        // Main burst — count and speed scale with size
        const count = Math.floor((100 + Math.random() * 80) * s)
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.25
          let speed
          if (this.shape === 'chrysanthemum') {
            speed = (4 + Math.random() * 5) * s
          } else if (this.shape === 'willow') {
            speed = (2 + Math.random() * 4) * s
          } else {
            speed = (3 + Math.random() * 6) * s
          }
          const isAlt = Math.random() > 0.5
          this.particles.push({
            x: this.x, y: this.y,
            vx: Math.cos(angle) * speed * (0.85 + Math.random() * 0.3),
            vy: Math.sin(angle) * speed * (0.85 + Math.random() * 0.3),
            life: 1,
            decay: (Math.random() * 0.012 + 0.005) / (s * 0.5),
            size: (Math.random() * 2.5 + 0.8) * s,
            color: isAlt ? this.color2 : this.color,
            gravity: this.shape === 'willow' ? 0.12 : 0.06,
            twinkle: Math.random() * Math.PI * 2,
            twinkleSpeed: Math.random() * 0.2 + 0.1,
            tail: this.shape === 'willow',
          })
        }
        // Outer ring flash
        const ringCount = Math.floor(28 * s)
        for (let i = 0; i < ringCount; i++) {
          const angle = (i / ringCount) * Math.PI * 2
          this.particles.push({
            x: this.x, y: this.y,
            vx: Math.cos(angle) * 11 * s,
            vy: Math.sin(angle) * 11 * s,
            life: 1, decay: 0.04,
            size: 1.2 * s, color: '#ffffff',
            gravity: 0.04, twinkle: 0, twinkleSpeed: 0, tail: false,
          })
        }
        // Shimmer sparks (tiny)
        if (s > 1) {
          for (let i = 0; i < 30; i++) {
            const angle = Math.random() * Math.PI * 2
            const spd = Math.random() * 2 * s
            this.particles.push({
              x: this.x, y: this.y,
              vx: Math.cos(angle) * spd,
              vy: Math.sin(angle) * spd,
              life: 1, decay: 0.03,
              size: 0.5, color: '#fffde7',
              gravity: 0.02, twinkle: 0, twinkleSpeed: 0, tail: false,
            })
          }
        }
      }

      update() {
        if (!this.exploded) {
          this.trail.push({ x: this.x, y: this.y })
          if (this.trail.length > 10) this.trail.shift()
          this.y -= this.speed
          if (this.y <= this.targetY) this.explode()
        } else {
          this.particles.forEach(p => {
            p.twinkle += p.twinkleSpeed
            p.x += p.vx
            p.y += p.vy
            p.vy += p.gravity
            p.vx *= 0.975
            p.vy *= 0.975
            p.life -= p.decay
          })
          this.particles = this.particles.filter(p => p.life > 0)
          if (this.particles.length === 0) this.alive = false
        }
      }

      draw() {
        if (!this.exploded) {
          // Glowing trail
          this.trail.forEach((p, i) => {
            const a = (i / this.trail.length)
            ctx.save()
            ctx.globalAlpha = a * 0.6
            const tg = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 3 * this.scale)
            tg.addColorStop(0, '#ffffff')
            tg.addColorStop(0.5, this.color)
            tg.addColorStop(1, 'transparent')
            ctx.beginPath()
            ctx.arc(p.x, p.y, 3 * this.scale, 0, Math.PI * 2)
            ctx.fillStyle = tg
            ctx.fill()
            ctx.restore()
          })
          // Rocket head
          ctx.save()
          const rg = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, 7 * this.scale)
          rg.addColorStop(0, '#ffffff')
          rg.addColorStop(0.4, this.color)
          rg.addColorStop(1, 'transparent')
          ctx.beginPath()
          ctx.arc(this.x, this.y, 7 * this.scale, 0, Math.PI * 2)
          ctx.fillStyle = rg
          ctx.fill()
          ctx.restore()
        } else {
          this.particles.forEach(p => {
            ctx.save()
            const twinkle = p.twinkleSpeed > 0 ? (Math.sin(p.twinkle) * 0.4 + 0.6) : 1
            ctx.globalAlpha = Math.max(0, p.life * twinkle)
            // Outer glow
            const pg = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 4.5)
            pg.addColorStop(0, p.color)
            pg.addColorStop(0.25, p.color + 'aa')
            pg.addColorStop(1, 'transparent')
            ctx.beginPath()
            ctx.arc(p.x, p.y, p.size * 4.5, 0, Math.PI * 2)
            ctx.fillStyle = pg
            ctx.fill()
            // Bright core
            ctx.beginPath()
            ctx.arc(p.x, p.y, p.size * 0.55, 0, Math.PI * 2)
            ctx.fillStyle = p.life > 0.5 ? '#ffffff' : p.color
            ctx.fill()
            ctx.restore()
          })
        }
      }
    }

    // Start with a big bang — many fireworks at once
    let fireworks = []
    // Initial volley: mix of big (scale 2+), medium, small
    const initialSizes = [2.5, 2.0, 1.6, 1.3, 1.0, 0.7, 0.6, 2.2, 1.5, 0.8, 1.8, 0.65]
    initialSizes.forEach((s, i) => {
      const fw = new Firework(s)
      fw.y = H - i * 12   // stagger launch heights so they don't all explode at once
      fireworks.push(fw)
    })

    // Background — pure dark with subtle neon glow
    function drawBg() {
      ctx.save()
      // Very subtle vignette glow from center
      const vg = ctx.createRadialGradient(W/2, H/2, 0, W/2, H/2, Math.max(W, H) * 0.7)
      vg.addColorStop(0, 'rgba(60,10,120,0.12)')
      vg.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = vg
      ctx.fillRect(0, 0, W, H)
      ctx.restore()
    }

    function draw() {
      // Fade trail (motion blur feel) instead of full clear
      ctx.fillStyle = 'rgba(5,5,15,0.35)'
      ctx.fillRect(0, 0, W, H)

      t += 0.03
      elapsed++

      drawBg()

      // Keep launching new ones — maintain density of 12-18 alive
      fireworks = fireworks.filter(f => f.alive)
      // Continuously spawn with varied sizes
      while (fireworks.length < 15) {
        const s = Math.random() > 0.55 ? (1.6 + Math.random() * 1.2) : (0.5 + Math.random() * 0.9)
        fireworks.push(new Firework(s))
      }
      // Occasional extra big one
      if (elapsed % 45 === 0) fireworks.push(new Firework(2.5 + Math.random() * 0.8))

      fireworks.forEach(fw => { fw.update(); fw.draw() })

      // After ~4 seconds start fading
      if (elapsed > 220) {
        coverAlpha = Math.min(1, coverAlpha + 0.03)
        ctx.save()
        ctx.globalAlpha = coverAlpha
        ctx.fillStyle = '#05050f'
        ctx.fillRect(0, 0, W, H)
        ctx.restore()
        if (coverAlpha >= 1) { cancelAnimationFrame(raf); onDone(); return }
      }

      raf = requestAnimationFrame(draw)
    }
    draw()
    return () => cancelAnimationFrame(raf)
  }, [])

  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none' }} />
}

// ── BLIZZARD: Snowstorm + aurora borealis ──────────────────────────────────────
function BlizzardTransition({ onDone }) {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    const W = canvas.width = window.innerWidth
    const H = canvas.height = window.innerHeight

    let t = 0
    let coverAlpha = 0
    let raf

    const flakes = Array.from({ length: 180 }, (_, i) => ({
      x: Math.random() * W,
      y: Math.random() * H,
      r: Math.random() * 4 + 0.5,
      speed: Math.random() * 3.5 + 0.8,
      drift: (Math.random() - 0.5) * 2,
      phase: Math.random() * Math.PI * 2,
      phaseSpeed: Math.random() * 0.04 + 0.01,
      opacity: Math.random() * 0.7 + 0.3,
      delay: i * 3,
    }))

    const streaks = Array.from({ length: 40 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      len: Math.random() * 100 + 40,
      speed: Math.random() * 10 + 5,
      opacity: Math.random() * 0.1 + 0.03,
      vy: (Math.random() - 0.3) * 1,
    }))

    const auroras = [
      { y: 0.12, color: [0, 255, 150],  speed: 0.15, amp: 35, phase: 0,   width: 60 },
      { y: 0.22, color: [80, 130, 255], speed: 0.11, amp: 25, phase: 2.1, width: 50 },
      { y: 0.32, color: [200, 60, 255], speed: 0.19, amp: 18, phase: 4.2, width: 40 },
      { y: 0.42, color: [0, 200, 255],  speed: 0.13, amp: 22, phase: 1.0, width: 45 },
    ]

    let frame = 0

    function draw() {
      ctx.clearRect(0, 0, W, H)
      t += 0.016
      frame++

      // Aurora bands
      auroras.forEach(a => {
        const baseY = H * a.y
        ctx.save()
        ctx.beginPath()
        ctx.moveTo(0, baseY)
        for (let x = 0; x <= W; x += 5) {
          const y = baseY
            + Math.sin((x / W) * Math.PI * 3.5 + t * a.speed + a.phase) * a.amp
            + Math.sin((x / W) * Math.PI * 7 - t * a.speed * 0.8) * (a.amp * 0.35)
        ctx.lineTo(x, y)
        }
        const [r, g, b] = a.color
        const pulse = 0.14 + Math.sin(t * a.speed * 3 + a.phase) * 0.05
        // Top fade
        ctx.lineTo(W, baseY - a.amp * 3)
        ctx.lineTo(0, baseY - a.amp * 3)
        ctx.closePath()
        const grad = ctx.createLinearGradient(0, baseY - a.amp * 3, 0, baseY + a.amp * 2)
        grad.addColorStop(0, `rgba(${r},${g},${b},0)`)
        grad.addColorStop(0.4, `rgba(${r},${g},${b},${pulse})`)
        grad.addColorStop(0.7, `rgba(${r},${g},${b},${pulse * 0.6})`)
        grad.addColorStop(1, `rgba(${r},${g},${b},0)`)
        ctx.fillStyle = grad
        ctx.fill()
        // Glow line
        ctx.beginPath()
        ctx.moveTo(0, baseY)
        for (let x = 0; x <= W; x += 5) {
          const y = baseY + Math.sin((x / W) * Math.PI * 3.5 + t * a.speed + a.phase) * a.amp
          ctx.lineTo(x, y)
        }
        ctx.strokeStyle = `rgba(${r},${g},${b},${pulse * 2})`
        ctx.lineWidth = 2
        ctx.stroke()
        ctx.restore()
      })

      // Wind streaks
      streaks.forEach(s => {
        s.x -= s.speed
        s.y += s.vy
        if (s.x < -s.len) { s.x = W + 20; s.y = Math.random() * H }
        ctx.save()
        ctx.globalAlpha = s.opacity
        ctx.strokeStyle = 'rgba(200,230,255,0.8)'
        ctx.lineWidth = 0.7
        ctx.beginPath()
        ctx.moveTo(s.x, s.y)
        ctx.lineTo(s.x + s.len, s.y + s.vy * 5)
        ctx.stroke()
        ctx.restore()
      })

      // Snowflakes
      flakes.forEach((f, i) => {
        if (frame < f.delay / 3) return
        f.phase += f.phaseSpeed
        f.x += Math.sin(f.phase) * 0.7 + f.drift - 1.5
        f.y += f.speed
        if (f.y > H + 15) { f.y = -15; f.x = Math.random() * W }
        if (f.x < -10) f.x = W + 10
        if (f.x > W + 10) f.x = -10

        const sg = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.r * 3)
        sg.addColorStop(0, `rgba(220,240,255,${f.opacity})`)
        sg.addColorStop(0.5, `rgba(190,215,255,${f.opacity * 0.5})`)
        sg.addColorStop(1, 'rgba(150,200,255,0)')
        ctx.beginPath()
        ctx.arc(f.x, f.y, f.r * 3, 0, Math.PI * 2)
        ctx.fillStyle = sg
        ctx.fill()
        ctx.beginPath()
        ctx.arc(f.x, f.y, f.r * 0.6, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(240,248,255,${f.opacity * 0.9})`
        ctx.fill()
      })

      // Cover sweeps in from top
      if (frame > 80) {
        coverAlpha = Math.min(1, coverAlpha + 0.018)
        ctx.save()
        ctx.globalAlpha = coverAlpha
        ctx.fillStyle = '#0a0f1e'
        ctx.fillRect(0, 0, W, H)
        ctx.restore()
      }

      if (coverAlpha >= 1) { cancelAnimationFrame(raf); onDone(); return }
      raf = requestAnimationFrame(draw)
    }
    draw()
    return () => cancelAnimationFrame(raf)
  }, [])

  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none' }} />
}

// ── CLASSIC: Ink blot expand + gold filigree + paper texture ──────────────────
function ClassicTransition({ onDone }) {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    const W = canvas.width = window.innerWidth
    const H = canvas.height = window.innerHeight

    let t = 0
    let progress = 0
    let raf

    // Ink blots expanding from corners and center
    const inkBlots = [
      { x: 0,     y: 0,     r: 0, maxR: Math.sqrt(W*W + H*H) * 0.7, speed: 8,  delay: 0 },
      { x: W,     y: 0,     r: 0, maxR: Math.sqrt(W*W + H*H) * 0.65, speed: 7, delay: 10 },
      { x: W/2,   y: H/2,   r: 0, maxR: Math.sqrt(W*W + H*H) * 0.6, speed: 9,  delay: 5 },
      { x: 0,     y: H,     r: 0, maxR: Math.sqrt(W*W + H*H) * 0.6, speed: 6,  delay: 15 },
      { x: W,     y: H,     r: 0, maxR: Math.sqrt(W*W + H*H) * 0.65, speed: 7, delay: 8 },
    ]

    // Golden filigree lines
    const filigrees = Array.from({ length: 30 }, (_, i) => ({
      x: Math.random() * W,
      y: Math.random() * H,
      angle: Math.random() * Math.PI * 2,
      len: Math.random() * 60 + 20,
      branches: Math.floor(Math.random() * 3) + 1,
      life: 0,
      speed: Math.random() * 0.04 + 0.02,
      delay: Math.floor(Math.random() * 40),
      color: Math.random() > 0.5 ? '#b7950b' : '#d4ac0d',
    }))

    // Confetti / paper pieces
    const papers = Array.from({ length: 50 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H - H,
      vx: (Math.random() - 0.5) * 3,
      vy: Math.random() * 3 + 1,
      rot: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.1,
      w: Math.random() * 18 + 6,
      h: Math.random() * 12 + 4,
      color: ['#f1f5f9','#e2e8f0','#cbd5e1','#fef9c3','#fef3c7','#dbeafe'][Math.floor(Math.random() * 6)],
      opacity: Math.random() * 0.7 + 0.3,
    }))

    let frame = 0

    function drawFiligree(f) {
      if (f.life <= 0) return
      ctx.save()
      ctx.translate(f.x, f.y)
      ctx.globalAlpha = Math.min(1, f.life * 2) * 0.7
      ctx.strokeStyle = f.color
      ctx.lineWidth = 0.8
      for (let b = 0; b < f.branches; b++) {
        const a = f.angle + (b / f.branches) * Math.PI * 2
        const l = f.len * Math.min(1, f.life)
        ctx.beginPath()
        ctx.moveTo(0, 0)
        ctx.bezierCurveTo(
          Math.cos(a + 0.3) * l * 0.4, Math.sin(a + 0.3) * l * 0.4,
          Math.cos(a - 0.3) * l * 0.8, Math.sin(a - 0.3) * l * 0.8,
          Math.cos(a) * l, Math.sin(a) * l
        )
        ctx.stroke()
        // Tip dot
        ctx.beginPath()
        ctx.arc(Math.cos(a) * l, Math.sin(a) * l, 2, 0, Math.PI * 2)
        ctx.fillStyle = f.color
        ctx.fill()
      }
      ctx.restore()
    }

    function draw() {
      ctx.clearRect(0, 0, W, H)
      t += 0.025
      frame++
      progress = Math.min(1, progress + 0.018)

      // White base
      ctx.fillStyle = '#f1f5f9'
      ctx.fillRect(0, 0, W, H)

      // Filigree decorations
      filigrees.forEach(f => {
        if (frame > f.delay) f.life = Math.min(1, f.life + f.speed)
        drawFiligree(f)
      })

      // Paper confetti
      papers.forEach(p => {
        p.x += p.vx + Math.sin(t * 2 + p.x * 0.01) * 0.4
        p.y += p.vy
        p.rot += p.rotSpeed
        if (p.y > H + 30) { p.y = -30; p.x = Math.random() * W }
        ctx.save()
        ctx.globalAlpha = p.opacity * 0.8
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rot)
        ctx.fillStyle = p.color
        ctx.beginPath()
        ctx.roundRect(-p.w / 2, -p.h / 2, p.w, p.h, 1)
        ctx.fill()
        // Fold line
        ctx.globalAlpha = 0.1
        ctx.strokeStyle = '#94a3b8'
        ctx.lineWidth = 0.5
        ctx.beginPath()
        ctx.moveTo(-p.w / 2, 0)
        ctx.lineTo(p.w / 2, 0)
        ctx.stroke()
        ctx.restore()
      })

      // Ink blots
      inkBlots.forEach(b => {
        if (frame > b.delay) b.r = Math.min(b.maxR, b.r + b.speed)
        if (b.r <= 0) return
        const ib = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r)
        ib.addColorStop(0, 'rgba(15,23,42,1)')
        ib.addColorStop(0.7, 'rgba(15,23,42,0.98)')
        ib.addColorStop(0.9, 'rgba(30,41,59,0.95)')
        ib.addColorStop(1, 'rgba(30,41,59,0)')
        ctx.beginPath()
        // Irregular ink blob edge
        ctx.save()
        ctx.translate(b.x, b.y)
        ctx.beginPath()
        const pts = 32
        for (let i = 0; i <= pts; i++) {
          const a = (i / pts) * Math.PI * 2
          const wobble = b.r * (0.9 + Math.sin(a * 7 + t * 2) * 0.06 + Math.sin(a * 13 - t * 3) * 0.03)
          const px = Math.cos(a) * wobble
          const py = Math.sin(a) * wobble
          i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)
        }
        ctx.closePath()
        ctx.fillStyle = ib
        ctx.fill()
        // Blue shimmer ring
        ctx.strokeStyle = 'rgba(37,99,235,0.15)'
        ctx.lineWidth = 3
        ctx.stroke()
        ctx.restore()
      })

      // Check if fully covered
      const allDone = inkBlots.every(b => b.r >= b.maxR * 0.98)
      if (allDone) {
        ctx.fillStyle = '#f1f5f9'
        ctx.globalAlpha = Math.min(1, (frame - 80) * 0.04)
        ctx.fillRect(0, 0, W, H)
        if (ctx.globalAlpha >= 1) { cancelAnimationFrame(raf); onDone(); return }
      }

      raf = requestAnimationFrame(draw)
    }
    draw()
    return () => cancelAnimationFrame(raf)
  }, [])

  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none' }} />
}

// ── Dispatcher ────────────────────────────────────────────────────────────────
export default function ThemeTransition({ themeKey, onDone }) {
  if (themeKey === 'forest')   return <ForestTransition   onDone={onDone} />
  if (themeKey === 'ocean')    return <OceanTransition    onDone={onDone} />
  if (themeKey === 'fire')     return <FireTransition     onDone={onDone} />
  if (themeKey === 'neon')     return <NeonTransition     onDone={onDone} />
  if (themeKey === 'blizzard') return <BlizzardTransition onDone={onDone} />
  if (themeKey === 'classic')  return <ClassicTransition  onDone={onDone} />
  return null
}